
import { Policy, Acknowledgment } from "@/types";
import { Dispatch, SetStateAction } from "react";

/**
 * Operations related to updating and publishing policies
 */
export const usePolicyUpdate = (
  policies: Policy[],
  setPolicies: Dispatch<SetStateAction<Policy[]>>,
  acknowledgments: Acknowledgment[],
  setAcknowledgments: Dispatch<SetStateAction<Acknowledgment[]>>,
  saveToStorage: (updatedPolicies: Policy[], updatedAcknowledgments?: Acknowledgment[]) => void
) => {
  const updatePolicy = (
    policyId: string,
    updates: Partial<Policy>,
    asDraft: boolean = false
  ) => {
    let updatedPolicies = [...policies];
    const existingPolicy = policies.find(p => p.id === policyId);
    
    if (!existingPolicy) return;
    
    if (asDraft) {
      // Check if this is already a draft, if so, just update it
      if (existingPolicy.isDraft) {
        updatedPolicies = updatedPolicies.map(p =>
          p.id === policyId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
        );
      } else {
        // Create a new draft version based on the existing policy
        const draftPolicy: Policy = {
          ...existingPolicy,
          ...updates,
          id: Math.random().toString(36).substr(2, 9),
          isDraft: true,
          originalPolicyId: existingPolicy.id,
          updatedAt: new Date().toISOString()
        };
        
        updatedPolicies.push(draftPolicy);
      }
    } else {
      // Regular update to the policy
      updatedPolicies = updatedPolicies.map(p =>
        p.id === policyId ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p
      );
    }
    
    setPolicies(updatedPolicies);
    saveToStorage(updatedPolicies);
  };

  const publishDraftPolicy = (draftPolicyId: string) => {
    const draftPolicy = policies.find(p => p.id === draftPolicyId && p.isDraft);
    if (!draftPolicy || !draftPolicy.originalPolicyId) return;
    
    const originalPolicy = policies.find(p => p.id === draftPolicy.originalPolicyId);
    if (!originalPolicy) return;
    
    const updatedPolicies = policies.map(p => {
      if (p.id === originalPolicy.id) {
        // Update the original policy with draft content
        return {
          ...p,
          title: draftPolicy.title,
          description: draftPolicy.description,
          content: draftPolicy.content,
          updatedAt: new Date().toISOString(),
          version: (parseFloat(p.version) + 0.1).toFixed(1),
          previousVersions: [
            ...(p.previousVersions || []),
            {
              version: p.version,
              content: p.content,
              updatedAt: p.updatedAt
            }
          ],
          editLog: [
            ...(p.editLog || []),
            ...(draftPolicy.editLog || [])
          ]
        };
      }
      return p;
    }).filter(p => p.id !== draftPolicyId); // Remove the draft
    
    setPolicies(updatedPolicies);
    saveToStorage(updatedPolicies);
  };

  const archivePolicy = (policyId: string) => {
    const updatedPolicies = policies.map(p => 
      p.id === policyId 
        ? { ...p, isArchived: true, updatedAt: new Date().toISOString() } 
        : p
    );
    
    setPolicies(updatedPolicies);
    saveToStorage(updatedPolicies);
  };

  const unarchivePolicy = (policyId: string) => {
    const updatedPolicies = policies.map(p => 
      p.id === policyId 
        ? { ...p, isArchived: false, updatedAt: new Date().toISOString() } 
        : p
    );
    
    setPolicies(updatedPolicies);
    saveToStorage(updatedPolicies);
  };

  return {
    updatePolicy,
    publishDraftPolicy,
    archivePolicy,
    unarchivePolicy
  };
};
